import * as Api from '@ViewModels';
import { History } from 'history';
import React from 'react';
import { UseFormClearErrors, UseFormGetValues, UseFormReset, UseFormSetValue } from 'react-hook-form';
import { toast } from 'sonner';
import { ErrorMessageViewModel } from '../../../../../extViewmodels/ViewModels';
import {
	invalidateInfiniteSurveys,
	invalidateSurveyById,
	useAccountStripeApiPublicApiKeys,
	useEventRegistrationMutation,
	useImageAttachmentMutation,
	useReorderEventRegistrationSurveyFieldsMutation,
	useUpdateEventSurveyMutation,
} from '../../../../../queries';
import { FieldStateManager } from '../../shared/components/AdditionalQuestions/types';
import { patchFieldTypesFromServerResponse } from '../../shared/utils/patchFieldTypesFromServerResponse';
import { EventRegistrationFormData, TabValues } from '../utils';

interface UseEventRegistrationMutationsProps {
	impersonationContext?: Api.IImpersonationContext;
	history: History;
	errorMessages: ErrorMessageViewModel;
	logApiError: (message: string, error: Api.IOperationResultNoValue) => void;
	getValues: UseFormGetValues<EventRegistrationFormData>;
	reset: UseFormReset<EventRegistrationFormData>;
	setValue: UseFormSetValue<EventRegistrationFormData>;
	clearErrors: UseFormClearErrors<EventRegistrationFormData>;
	setState: React.Dispatch<
		React.SetStateAction<{
			enableRequestAttendeesToRSVP: boolean;
			fileInputModalOpen: boolean;
			now: Date;
			selectedImage: Api.IFileAttachment | null;
			customFormIsDisabled: boolean;
			selectedTab: TabValues;
			showEditSurveyName: boolean;
			showReorderModal: boolean;
		}>
	>;
	onReorderSuccess?: (fields: Api.IFormField<string>[]) => void;
	/**
	 * Lifted per-field dirty manager. After a successful save, the mutation handlers call
	 * resetDirtyState + updateInitialStates so the "unsaved" indicators on each question
	 * clear and the next edit registers against the just-saved baseline.
	 */
	fieldStateManager?: FieldStateManager;
}

// Hook for mutations and queries
export const useEventRegistrationSurveyMutations = ({
	impersonationContext,
	history,
	errorMessages,
	logApiError,
	getValues,
	reset,
	setValue,
	clearErrors,
	setState,
	onReorderSuccess,
	fieldStateManager,
}: UseEventRegistrationMutationsProps) => {
	// Query for Stripe API keys
	const stripePublicKeys = useAccountStripeApiPublicApiKeys({
		impersonationContext,
	});

	const publishableApiKeys = React.useMemo(() => {
		return stripePublicKeys?.data ? { publishableKey: stripePublicKeys.data } : undefined;
	}, [stripePublicKeys]);

	// Mutation for event registration
	const eventRegistrationMutation = useEventRegistrationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to create event registration survey');
			logApiError('EventRegistrationSurvey-Error', error);
		},
		onSuccess: async (val: Api.IEventRegistrationSurvey) => {
			toast.success('Event registration survey created');

			history.replace(`/surveys/event/edit/${val.id}`);
			invalidateInfiniteSurveys();
			// Patch `_type` onto locally-added fields using the server response so `hasBeenSaved`
			// (which is `!!field._type && !isClone`) flips to true and the "Not Saved" indicator
			// clears. We deliberately do NOT replace the full field array — the PUT response omits
			// `options` (those only come back via `?expand=options` on the GET), so a blanket
			// overwrite wipes dropdown choices and breaks any visibility rules that reference them.
			patchFieldTypesFromServerResponse(val?.customForm?.fields, getValues().customForm?.fields, setValue);
			// Reset form dirty state while keeping current values (now including patched _type)
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
			clearErrors();
			// Clear per-field dirty indicators and re-baseline initial states to the saved values
			fieldStateManager?.resetDirtyState();
			fieldStateManager?.updateInitialStates(formValues.customForm?.fields ?? []);
		},
	});

	// Mutation for survey updates
	const updateSurveyEventMutation = useUpdateEventSurveyMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to update the event');
			logApiError('EventRegistrationSurvey-Error', error);
		},
		onSuccess: (data?: Api.IEventRegistrationSurvey) => {
			toast.success('Updates event success');
			invalidateInfiniteSurveys();
			invalidateSurveyById();
			// See create-onSuccess for why we patch rather than overwrite.
			patchFieldTypesFromServerResponse(data?.customForm?.fields, getValues().customForm?.fields, setValue);
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
			clearErrors();
			fieldStateManager?.resetDirtyState();
			fieldStateManager?.updateInitialStates(formValues.customForm?.fields ?? []);
		},
	});

	// Mutation for image attachments
	const imageAttachmentMutation = useImageAttachmentMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to upload image');
			logApiError('UploadImageEventRegistrationSurvey-Error', error);
		},
		onSuccess: result => {
			setState(prevState => ({
				...prevState,
				selectedImage: result[0],
			}));
		},
	});

	const reorderSurveyFieldsMutation = useReorderEventRegistrationSurveyFieldsMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to reorder event registration survey fields');
			logApiError('EventRegistrationReorderSurveyFields-Error', error);
		},
		onSuccess: data => {
			toast.success('Event registration survey fields reordered successfully');
			if (onReorderSuccess && data?.fields) {
				onReorderSuccess(data.fields);
			}
		},
	});

	return {
		publishableApiKeys,
		eventRegistrationMutation,
		updateSurveyEventMutation,
		imageAttachmentMutation,
		reorderSurveyFieldsMutation,
	};
};
